/**
 * LT-15 — Settings → Job Types.
 *
 * User-managed list of the workspace's job types. This is the source of
 * truth. Four other collections tag against `key`:
 *
 *   • master_phase_list.applies_to_job_types
 *   • ao_library_items.applies_to_job_types
 *   • benchmark_config.base_rate_per_m2_by_job_type
 *   • milestone_templates.job_type
 *
 * `key` is IMMUTABLE — a rename would silently orphan every tag above.
 * `label` is editable and is the only thing every display site renders.
 * Archive hides a type from the Create-tender dropdown, but tenders
 * already using it continue to resolve its label (see useJobTypeLibrary).
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, ChevronUp, ChevronDown, Archive as ArchiveIcon, ArchiveRestore,
  Info, Loader2, Save, X, Pencil, LayoutTemplate,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import {
  Card, Field, Input, PrimaryButton, QuietButton,
} from "../../components/UI";

const cls = (...xs) => xs.filter(Boolean).join(" ");

export function JobTypesTab() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState("");
  const [saving, setSaving] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");

  const load = () =>
    api.get("/settings/job-types?include_archived=true")
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data.slice() : [];
        list.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
        setRows(list);
      })
      .catch(() => toast.error("Failed to load job types"))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);
  // LT-21b — live job templates, to link each type to its default template.
  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data || [])).catch(() => setTemplates([]));
  }, []);
  const templateFor = (key) => templates.find((t) => t.job_type === key);

  const add = async () => {
    const label = adding.trim();
    if (!label) return;
    setSaving(true);
    try {
      await api.post("/settings/job-types", { label });
      setAdding("");
      toast.success("Job type added");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally { setSaving(false); }
  };

  const startRename = (row) => {
    setRenamingId(row.id);
    setRenameDraft(row.label);
  };
  const cancelRename = () => { setRenamingId(null); setRenameDraft(""); };
  const commitRename = async () => {
    const label = renameDraft.trim();
    if (!renamingId) return cancelRename();
    if (!label) { cancelRename(); return; }
    try {
      await api.patch(`/settings/job-types/${renamingId}`, { label });
      toast.success("Job type renamed");
      cancelRename();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    }
  };

  // Reorder — swap two adjacent rows and PATCH each with its new
  // display_order. Two calls, in parallel; roll back on failure.
  const move = async (idx, dir) => {
    const swap = idx + dir;
    if (swap < 0 || swap >= rows.length) return;
    const a = rows[idx];
    const b = rows[swap];
    // Optimistic swap of both display_order values.
    const next = rows.slice();
    next[idx] = { ...b, display_order: a.display_order };
    next[swap] = { ...a, display_order: b.display_order };
    setRows(next);
    try {
      await Promise.all([
        api.patch(`/settings/job-types/${a.id}`, { display_order: b.display_order }),
        api.patch(`/settings/job-types/${b.id}`, { display_order: a.display_order }),
      ]);
    } catch (e) {
      toast.error("Reorder failed");
      load();
    }
  };

  const toggleArchive = async (row) => {
    try {
      await api.patch(`/settings/job-types/${row.id}`, { archived: !row.archived });
      toast.success(row.archived ? "Job type restored" : "Job type archived");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (loading) return <div className="text-ink-3" style={{ fontSize: 12 }}>Loading…</div>;

  return (
    <Card className="p-5" data-testid="job-types-tab">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <h3 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
            Job Types
          </h3>
          <p className="text-ink-3 mt-1 flex items-start gap-1.5 max-w-2xl" style={{ fontSize: 12 }}>
            <Info size={12} className="mt-0.5 flex-shrink-0 text-ink-4" />
            <span>
              The workspace's list of job types. This is where they come from — every
              tender's job type, every phase or A&O item tagged "applies to this job type",
              every £/m² base rate, and every milestone template all reference the <span className="font-mono">key</span> below.
              Rename a <strong>label</strong> freely — it's the display name only.
              The <span className="font-mono">key</span> is fixed the moment a job type is created; renaming it would silently
              orphan those tags. Archiving hides a type from the New Tender dropdown but leaves
              existing tenders using it fully resolvable. Each type can have one live <strong>job template</strong>
              (Libraries → Job Templates) that new tenders of that type start from.
            </span>
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-1.5 mb-4 mt-4">
        {rows.map((row, i) => (
          <div
            key={row.id}
            className={cls(
              "rounded-md border bg-surface",
              row.archived ? "border-line-soft bg-surface-2/40" : "border-line",
            )}
            data-testid={`job-type-row-${row.key}`}
          >
            <div className="flex items-center justify-between gap-3 px-3 py-2.5">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="font-mono tnum text-ink-3 flex-shrink-0" style={{ fontSize: 11 }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                {renamingId === row.id ? (
                  <input
                    type="text"
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitRename();
                      if (e.key === "Escape") cancelRename();
                    }}
                    className="flex-1 min-w-0 rounded border border-accent/40 bg-surface-2 px-2 py-1 text-ink outline-none"
                    style={{ fontSize: 13 }}
                    autoFocus
                    data-testid={`job-type-rename-input-${row.key}`}
                  />
                ) : (
                  <button
                    className={cls(
                      "text-left flex-1 min-w-0 hover:text-accent",
                      row.archived ? "text-ink-3" : "text-ink",
                    )}
                    style={{ fontSize: 13, fontWeight: row.archived ? 400 : 500 }}
                    onClick={() => startRename(row)}
                    title="Click to rename the label"
                    data-testid={`job-type-label-${row.key}`}
                  >
                    {row.label}
                    {row.archived && (
                      <span className="ml-2 rounded-full border border-line-soft px-2 py-0.5 text-ink-3"
                        style={{ fontSize: 10, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                        Archived
                      </span>
                    )}
                  </button>
                )}
                <span
                  className="font-mono text-ink-4 rounded bg-surface-2 px-1.5 py-0.5"
                  style={{ fontSize: 10.5 }}
                  title="Immutable machine key — what phases, A&O items, £/m² rates and milestone templates are tagged against. Change the label above; the key is fixed at creation."
                  data-testid={`job-type-key-${row.key}`}
                >
                  {row.key}
                </span>
                {!row.archived && (() => {
                  const tpl = templateFor(row.key);
                  return tpl ? (
                    <button
                      type="button"
                      onClick={() => navigate(`/templates/${tpl.id}`)}
                      className="inline-flex items-center gap-1 text-ink-3 hover:text-accent flex-shrink-0 truncate max-w-[220px]"
                      style={{ fontSize: 11 }}
                      title={`Default job template: ${tpl.name}`}
                      data-testid={`job-type-template-${row.key}`}
                    >
                      <LayoutTemplate size={11} /> {tpl.name}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigate(`/templates?new=1&job_type=${encodeURIComponent(row.key)}`)}
                      className="inline-flex items-center gap-1 text-ink-4 hover:text-accent flex-shrink-0"
                      style={{ fontSize: 11 }}
                      title="No job template yet — create the default template for this job type"
                      data-testid={`job-type-template-create-${row.key}`}
                    >
                      <Plus size={10} /> template
                    </button>
                  );
                })()}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  className="h-7 w-7 rounded text-ink-3 hover:bg-surface-2 hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid={`job-type-up-${row.key}`}
                ><ChevronUp size={13} className="mx-auto" /></button>
                <button
                  onClick={() => move(i, 1)}
                  disabled={i === rows.length - 1}
                  className="h-7 w-7 rounded text-ink-3 hover:bg-surface-2 hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid={`job-type-down-${row.key}`}
                ><ChevronDown size={13} className="mx-auto" /></button>
                <button
                  onClick={() => toggleArchive(row)}
                  className="h-7 px-2 rounded text-ink-3 hover:bg-surface-2 hover:text-ink"
                  title={row.archived ? "Restore" : "Archive"}
                  data-testid={`job-type-archive-${row.key}`}
                >
                  {row.archived ? <ArchiveRestore size={12} /> : <ArchiveIcon size={12} />}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-end gap-2">
        <Field label="Add a job type" hint="A key is auto-generated from the label; you can rename the label later but not the key.">
          <Input
            value={adding}
            onChange={setAdding}
            placeholder="e.g. Barn Conversion"
            onKeyDown={(e) => e.key === "Enter" && add()}
            data-testid="job-type-add-input"
          />
        </Field>
        <PrimaryButton
          onClick={add}
          icon={saving ? Loader2 : Plus}
          disabled={!adding.trim() || saving}
          data-testid="job-type-add-btn"
        >
          Add
        </PrimaryButton>
      </div>
    </Card>
  );
}
